import { getIntersectionRoot } from '@theme/scroll-container';

const PARTICLE_COLORS = [
  [31, 115, 238],
  [40, 215, 192],
  [226, 239, 255],
];

class XstoFooterParticles extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.footer = this.closest('.xsto-footer');
    this.canvas = this.querySelector('canvas');
    this.context = this.canvas?.getContext('2d', { alpha: true });
    if (!this.footer || !this.canvas || !this.context) return;

    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.isLowPower =
      navigator.connection?.saveData === true ||
      Number(navigator.hardwareConcurrency) <= 2 ||
      Number(navigator.deviceMemory) <= 2;
    this.isVisible = false;
    this.animationFrame = null;
    this.particles = [];
    this.exclusionAreas = [];

    this.handleFrame = this.drawFrame.bind(this);
    this.handleVisibility = this.syncAnimation.bind(this);
    this.handleMotionChange = this.updateMotionPreference.bind(this);
    this.handleResize = this.resize.bind(this);

    this.motionQuery.addEventListener('change', this.handleMotionChange);
    document.addEventListener('visibilitychange', this.handleVisibility);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.handleResize);
      this.resizeObserver.observe(this.footer);
    } else {
      window.addEventListener('resize', this.handleResize, { passive: true });
    }

    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          this.isVisible = entry.isIntersecting;
          this.syncAnimation();
        },
        { root: getIntersectionRoot(), rootMargin: '160px 0px', threshold: 0.01 }
      );
      this.intersectionObserver.observe(this.footer);
    } else {
      this.isVisible = true;
    }

    document.fonts?.ready.then(() => {
      if (this.isConnected) this.resize();
    });

    this.updateMotionPreference();
  }

  disconnectedCallback() {
    this.stopAnimation();
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.motionQuery?.removeEventListener('change', this.handleMotionChange);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    window.removeEventListener('resize', this.handleResize);
    this.initialized = false;
  }

  updateMotionPreference() {
    // Accessibility: fully hide when the user asks for reduced motion.
    // Low-power devices keep a static starfield so the footer does not look empty.
    this.prefersReducedMotion = this.motionQuery.matches;
    this.isStatic = this.isLowPower || this.prefersReducedMotion;
    this.classList.toggle('is-static', this.isStatic);
    this.classList.toggle('is-reduced', this.prefersReducedMotion);

    if (this.prefersReducedMotion) {
      this.stopAnimation();
      this.context.clearRect(0, 0, this.width || 0, this.height || 0);
      return;
    }

    this.resize();
    this.syncAnimation();
  }

  resize() {
    if (this.prefersReducedMotion || !this.footer || !this.context) return;

    const bounds = this.footer.getBoundingClientRect();
    const width = Math.max(1, Math.round(bounds.width));
    const height = Math.max(1, Math.round(bounds.height));
    const isMobile = width < 750;
    const pixelRatio = Math.min(window.devicePixelRatio || 1, isMobile ? 1.5 : 2);

    if (width === this.width && height === this.height && pixelRatio === this.pixelRatio) {
      this.measureExclusionAreas(bounds);
      if (this.isStatic) this.drawStaticFrame();
      return;
    }

    this.width = width;
    this.height = height;
    this.pixelRatio = pixelRatio;
    this.isMobile = isMobile;
    this.canvas.width = Math.round(width * pixelRatio);
    this.canvas.height = Math.round(height * pixelRatio);
    this.context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    this.createParticles();
    this.measureExclusionAreas(bounds);

    if (this.isStatic) this.drawStaticFrame();
  }

  createParticles() {
    const area = this.width * this.height;
    // Slightly denser than P1: still ambient, not a sparkle field.
    const minimum = this.isMobile ? 24 : 52;
    const maximum = this.isMobile ? 38 : 96;
    const density = this.isMobile ? 0.00009 : 0.00011;
    const count = Math.min(maximum, Math.max(minimum, Math.round(area * density)));

    this.particles = Array.from({ length: count }, () => {
      const placement = Math.random();
      // Bias to left/right gutters of the footer so text stays readable.
      // Remaining particles sit lower across the width as a soft base layer.
      const isLeft = placement < 0.38;
      const isRight = placement >= 0.38 && placement < 0.76;
      const x = isLeft
        ? this.width * (0.01 + Math.random() * 0.26)
        : isRight
          ? this.width * (0.72 + Math.random() * 0.26)
          : this.width * (0.12 + Math.random() * 0.76);
      const y =
        placement < 0.76
          ? this.height * (0.22 + Math.pow(Math.random(), 0.55) * 0.72)
          : this.height * (0.72 + Math.random() * 0.24);
      const colorRoll = Math.random();
      const colorIndex = colorRoll < 0.52 ? 0 : colorRoll < 0.84 ? 1 : 2;
      const isSoftGlow = !this.isMobile && Math.random() < 0.28;

      return {
        x,
        y,
        color: PARTICLE_COLORS[colorIndex],
        radius: isSoftGlow ? 2.2 + Math.random() * 1.6 : 1 + Math.random() * 1.65,
        alpha: colorIndex === 2 ? 0.22 + Math.random() * 0.18 : 0.3 + Math.random() * 0.28,
        phase: Math.random() * Math.PI * 2,
        xRange: 10 + Math.random() * (this.isMobile ? 14 : 24),
        yRange: 5 + Math.random() * (this.isMobile ? 8 : 14),
        xSpeed: 0.00005 + Math.random() * 0.000055,
        ySpeed: 0.000075 + Math.random() * 0.00007,
        glow: isSoftGlow,
      };
    });
  }

  measureExclusionAreas(footerBounds) {
    const selectors = [
      '.xsto-footer__operator',
      '.xsto-footer h3',
      '.xsto-footer li',
      '.xsto-footer__bottom > span',
      '.xsto-footer__policies a',
    ];

    this.exclusionAreas = Array.from(this.footer.querySelectorAll(selectors.join(',')))
      .map((element) => element.getBoundingClientRect())
      .filter((bounds) => bounds.width > 0 && bounds.height > 0)
      .map((bounds) => ({
        x: bounds.left - footerBounds.left - 10,
        y: bounds.top - footerBounds.top - 7,
        width: bounds.width + 20,
        height: bounds.height + 14,
      }));
  }

  clearCanvas() {
    if (!this.context) return;
    this.context.clearRect(0, 0, this.width || 0, this.height || 0);
  }

  paintParticles(time = 0, animate = true) {
    const context = this.context;
    if (!context || !this.width || !this.height) return;

    this.clearCanvas();

    for (const particle of this.particles) {
      const x = animate
        ? particle.x + Math.sin(time * particle.xSpeed + particle.phase) * particle.xRange
        : particle.x;
      const y = animate
        ? particle.y + Math.cos(time * particle.ySpeed + particle.phase) * particle.yRange
        : particle.y;
      const breath = animate ? 0.88 + Math.sin(time * 0.00008 + particle.phase) * 0.12 : 1;
      const [red, green, blue] = particle.color;

      context.beginPath();
      context.arc(x, y, particle.radius, 0, Math.PI * 2);
      context.fillStyle = `rgba(${red}, ${green}, ${blue}, ${particle.alpha * breath})`;
      context.shadowBlur = particle.glow ? particle.radius * 7.5 : particle.radius * 1.2;
      context.shadowColor = particle.glow
        ? `rgba(${red}, ${green}, ${blue}, 0.42)`
        : `rgba(${red}, ${green}, ${blue}, 0.18)`;
      context.fill();
    }

    context.shadowBlur = 0;
    for (const area of this.exclusionAreas) {
      context.clearRect(area.x, area.y, area.width, area.height);
    }
  }

  drawStaticFrame() {
    if (this.prefersReducedMotion || !this.particles.length) return;
    this.paintParticles(0, false);
  }

  syncAnimation() {
    if (this.prefersReducedMotion) {
      this.stopAnimation();
      this.clearCanvas();
      return;
    }

    if (this.isStatic) {
      this.stopAnimation();
      if (this.isVisible && !document.hidden) this.drawStaticFrame();
      else this.clearCanvas();
      return;
    }

    const shouldAnimate = this.isVisible && !document.hidden;

    if (!shouldAnimate) {
      this.stopAnimation();
      return;
    }

    if (this.animationFrame === null) {
      this.animationFrame = window.requestAnimationFrame(this.handleFrame);
    }
  }

  stopAnimation() {
    if (this.animationFrame !== null) {
      window.cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  drawFrame(time) {
    this.animationFrame = null;
    if (this.isStatic || this.prefersReducedMotion || !this.isVisible || document.hidden) return;

    this.paintParticles(time, true);
    this.animationFrame = window.requestAnimationFrame(this.handleFrame);
  }
}

if (!customElements.get('xsto-footer-particles')) {
  customElements.define('xsto-footer-particles', XstoFooterParticles);
}
